import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { forkOutcome, listProjects } from '@/api';
import { isLoggedIn } from '@/auth/token';
import { projectIdFromSearch } from '@/pages/home/studioPersist';
import { forkIdFromSearch, guestStudioForkRedirect } from '@/pages/home/guestFork';
import { studioEntryFromSearch, type StudioEntry } from '@/pages/home/studioEntry';
import { completeStudioForkOnce } from '@/pages/landing/forkFlow';
import ComposeView from './ComposeView';

export default function Compose() {
  const loc = useLocation();
  const navigate = useNavigate();
  const projectId = projectIdFromSearch(loc.search);
  const forkId = forkIdFromSearch(loc.search);
  const guestForkTo = !isLoggedIn() ? guestStudioForkRedirect(loc.search, loc.pathname) : null;
  const [gate, setGate] = useState<StudioEntry>(
    studioEntryFromSearch(loc.search, isLoggedIn(), Boolean(projectId || forkId || !isLoggedIn())),
  );
  const [forkErr, setForkErr] = useState<string | null>(null);

  useEffect(() => {
    const next = studioEntryFromSearch(loc.search, isLoggedIn(), false);
    if (next !== 'wait') {
      setGate(next);
      return;
    }
    let cancelled = false;
    listProjects()
      .then(() => {
        if (!cancelled) setGate(studioEntryFromSearch(loc.search, isLoggedIn(), true));
      })
      .catch(() => {
        if (!cancelled) setGate('chooser');
      });
    return () => { cancelled = true; };
  }, [loc.search, projectId, forkId]);

  useEffect(() => {
    if (!isLoggedIn() || !forkId || projectId) return;
    setForkErr(null);
    void completeStudioForkOnce({
      outcomeId: forkId,
      loggedIn: true,
      navigate: (path) => navigate(path, { replace: true }),
      forkApi: forkOutcome,
    }).then((res) => {
      if (res.success) return;
      setForkErr(res.error || 'failed to fork outcome');
    });
  }, [forkId, projectId, navigate]);

  if (guestForkTo) return <Navigate to={guestForkTo} replace />;
  if (gate === 'chooser') return <Navigate to="/dashboard?view=projects" replace />;
  if (gate === 'wait') return null;
  if (forkId && !projectId) {
    if (forkErr) {
      return (
        <div className="min-h-screen bg-bg text-textp flex items-center justify-center px-4" data-testid="fork-error">
          <div className="max-w-md text-center">
            <p className="text-sm text-error">{forkErr}</p>
            <Link to="/playbooks" className="mt-4 inline-block text-sm text-accent hover:underline">
              Back to certified playbooks
            </Link>
          </div>
        </div>
      );
    }
    return null;
  }
  return <ComposeView projectId={projectId || ''} />;
}
